import { useMutation } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { transformErrors } from "helpers/utils";
import { Col, Form, Row } from "react-bootstrap";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";
import {
  CriarPrincipioAtivoDtoType,
  principioAtivoBreadcrumbItems,
} from "data/principiosAtivos";
import { usePermissionModal } from "contexts/PermissionModalContext";

const NovoPrincipioAtivo = () => {
  const navigate = useNavigate();
  const { showPermissionModal } = usePermissionModal();

  const form = useForm<CriarPrincipioAtivoDtoType>({
    defaultValues: {
      descricao: "",
    },
  });

  const mutation = useMutation<any, any, CriarPrincipioAtivoDtoType>({
    mutationFn: (p: CriarPrincipioAtivoDtoType) => {
      return axios.post("/api/principioAtivo", p);
    },
    onError: (error: any) => {
      // Check for permission error (403)
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        showPermissionModal("Você não possui permissão para cadastrar princípios ativos");
        return;
      }

      // Handle validation errors
      if (error.response?.data?.errors) {
        const formattedErrors = transformErrors(error.response.data.errors);
        for (const [field, error] of Object.entries(formattedErrors)) {
          form.setError(field as any, error as any);
        }
      }
    },
  });

  const onSubmit: SubmitHandler<CriarPrincipioAtivoDtoType> = (data) => {
    mutation.mutate(data);
  };

  if (mutation.isSuccess) {
    const id = mutation.data.data.id;
    return <Navigate to={`/cadastros/principios-ativos/${id}`} />;
  }

  return (
    <div>
      <FormProvider {...form}>
        <PageBreadcrumb items={principioAtivoBreadcrumbItems()} />
        <Form
          noValidate
          className="mb-9"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
            <div>
              <h2 className="mb-2">Cadastrar um novo princípio ativo</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Preencha as informações para cadastrar um novo princípio ativo
              </h5>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="phoenix-secondary"
                type="button"
                onClick={() => navigate("/cadastros/principios-ativos")}
              >
                Descartar
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={mutation.isPending}
                disabled={mutation.isPending}
                loadingPosition="start"
              >
                Salvar
              </Button>
            </div>
          </div>
          <Row className="g-5">
            <Col xs={12} xl={8}>
              <h4 className="mb-3">Informações do princípio ativo</h4>
              <Row className="g-2">
                <Form.Group
                  as={Col}
                  xs={12}
                  xl={12}
                  className="mb-5"
                  controlId="descricao"
                >
                  <Form.Control
                    placeholder="Descrição do princípio ativo"
                    {...form.register("descricao")}
                    required
                    isInvalid={!!form.formState.errors.descricao}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.descricao?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
            </Col>
          </Row>
        </Form>
      </FormProvider>
    </div>
  );
};

export default NovoPrincipioAtivo;
